import { Box, Flex, Text } from '@chakra-ui/react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FaTrash,
  FaMobileAlt,
  FaApple,
  FaAndroid,
  FaGlobe,
} from 'react-icons/fa';
import { formatDistanceToNow } from 'date-fns';
import { SectionCard, Tooltip } from '@themes/aegix';
import { DeviceToken } from '../../types/UserFormInitialValue';

const toTitleCase = (str: string) => {
  return str?.toLowerCase()?.replace(/\b\w/g, (char) => char?.toUpperCase());
};

interface UserDevicesSectionProps {
  devices: DeviceToken[];
  onRemoveDevice?: (tokenId: string, platform: string) => void;
  canDelete?: boolean;
}

const getPlatformIcon = (platform: string) => {
  const lowerPlatform = platform?.toLowerCase();
  if (lowerPlatform === 'ios') return <FaApple size={20} />;
  if (lowerPlatform === 'android') return <FaAndroid size={20} />;
  if (lowerPlatform === 'web') return <FaGlobe size={20} />;
  return <FaMobileAlt size={20} />;
};

const isDeviceExpiring = (updatedAt: string): boolean => {
  if (!updatedAt) return false;
  try {
    const updatedDate = new Date(updatedAt);
    const currentDate = new Date();
    const msPerDay = 1000 * 60 * 60 * 24;
    const daysSinceUpdate = Math.round(
      (currentDate.getTime() - updatedDate.getTime()) / msPerDay,
    );
    return daysSinceUpdate >= 30;
  } catch {
    return false;
  }
};

const getDeviceDisplayName = (token: DeviceToken): string => {
  if (token.device_model) {
    return token.device_model;
  }
  return toTitleCase(token.platform);
};

const getDeviceGroupKey = (token: DeviceToken): string => {
  if (token.device_model) {
    return `${token.device_model}|${token.platform}`;
  }
  return token.platform;
};

const truncateUserAgent = (userAgent: string, maxLength = 50): string => {
  if (!userAgent || userAgent.length <= maxLength) {
    return userAgent;
  }
  return userAgent.substring(0, maxLength) + '...';
};

interface GroupedDevice {
  key: string;
  displayName: string;
  platform: string;
  tokens: DeviceToken[];
  mostRecent: DeviceToken;
}

export const UserDevicesSection = ({
  devices,
  onRemoveDevice,
  canDelete = true,
}: UserDevicesSectionProps) => {
  const { t } = useTranslation();

  const groupedDevices = useMemo(() => {
    if (!devices || devices.length === 0) return [];

    const groups: Record<string, DeviceToken[]> = {};

    devices.forEach((token) => {
      const groupKey = getDeviceGroupKey(token);
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(token);
    });

    const groupedDeviceArray: GroupedDevice[] = Object.entries(groups).map(
      ([key, tokens]) => {
        const sortedTokens = tokens.sort(
          (a, b) =>
            new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
        );

        const mostRecent = sortedTokens[0];

        return {
          key,
          displayName: getDeviceDisplayName(mostRecent),
          platform: mostRecent.platform,
          tokens: sortedTokens,
          mostRecent,
        };
      },
    );

    return groupedDeviceArray.sort(
      (a, b) =>
        new Date(b.mostRecent.updated_at).getTime() -
        new Date(a.mostRecent.updated_at).getTime(),
    );
  }, [devices]);

  return (
    <SectionCard title={t('profile.devices', 'Devices')}>
      <Box textAlign={'start'}>
        {groupedDevices && groupedDevices.length > 0 ? (
          groupedDevices.map((deviceGroup: GroupedDevice) => (
            <Box key={deviceGroup.key} mb={3}>
              <Flex
                justifyContent={'space-between'}
                alignItems={'center'}
                p={3}
                border={'1px solid'}
                borderColor={'border.secondary'}
                borderRadius={'md'}
                bg={'surface.secondary'}
                data-e2e={`device-group-${deviceGroup.key}`}
              >
                <Flex gap={3} alignItems={'center'} flex={1}>
                  <Box color={'text.secondary'}>
                    {getPlatformIcon(deviceGroup.platform)}
                  </Box>
                  <Box flex={1}>
                    <Flex alignItems={'center'} gap={2} mb={1}>
                      <Text fontWeight={600} fontSize={'sm'}>
                        {deviceGroup.displayName}
                      </Text>
                      {isDeviceExpiring(deviceGroup.mostRecent.updated_at) && (
                        <Box
                          color="yellow.800"
                          px={2}
                          py={0.5}
                          bg="yellow.400"
                          borderRadius={'md'}
                          fontSize={'2xs'}
                          fontWeight={600}
                        >
                          {t('profile.expiring', 'Expiring')}
                        </Box>
                      )}
                      {deviceGroup.tokens.length > 1 && (
                        <Box
                          color="blue.800"
                          px={2}
                          py={0.5}
                          bg="blue.100"
                          borderRadius={'md'}
                          fontSize={'2xs'}
                          fontWeight={600}
                        >
                          {deviceGroup.tokens.length} {t('profile.tokens', 'tokens')}
                        </Box>
                      )}
                    </Flex>

                    <Text fontSize={'xs'} color={'text.secondary'} mb={1}>
                      {deviceGroup.mostRecent.os_version && (
                        <>{deviceGroup.mostRecent.os_version} • </>
                      )}
                      v{deviceGroup.mostRecent.app_version} • {t('profile.lastActive', 'Last active')}{' '}
                      {deviceGroup.mostRecent.updated_at
                        ? formatDistanceToNow(
                            new Date(deviceGroup.mostRecent.updated_at),
                            {
                              addSuffix: true,
                            },
                          )
                        : t('common.unknown', 'Unknown')}
                    </Text>

                    {deviceGroup.mostRecent.user_agent && (
                      <Tooltip
                        content={deviceGroup.mostRecent.user_agent}
                        positioning={{ placement: 'top-start' }}
                      >
                        <Text
                          fontSize={'2xs'}
                          color={'text.tertiary'}
                          cursor="help"
                          textDecoration="underline dotted"
                        >
                          {truncateUserAgent(deviceGroup.mostRecent.user_agent)}
                        </Text>
                      </Tooltip>
                    )}
                  </Box>
                </Flex>
                {canDelete && onRemoveDevice && (
                  <Box
                    as="button"
                    onClick={() =>
                      onRemoveDevice(
                        deviceGroup.mostRecent.id,
                        deviceGroup.platform,
                      )
                    }
                    color={'red.500'}
                    cursor={'pointer'}
                    p={2}
                    borderRadius={'md'}
                    _hover={{ bg: 'surface.dangerHover' }}
                    title={t('profile.removeDevice', 'Remove device')}
                    data-e2e={`removeDevice-${deviceGroup.mostRecent.id}`}
                  >
                    <FaTrash size={16} />
                  </Box>
                )}
              </Flex>
            </Box>
          ))
        ) : (
          <Text color={'text.secondary'} textAlign={'center'} py={4}>
            {t('profile.noActiveDevices', 'No active devices')}
          </Text>
        )}
        <Text fontSize={'xs'} color={'text.secondary'} mt={4}>
          {t('profile.removeDeviceSecurityNote', 'Remove devices no longer in-use to improve security')}
        </Text>
      </Box>
    </SectionCard>
  );
};

export default UserDevicesSection;
